import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { fmt } from '../helpers';

// ── กราฟแท่งเทียบสัดส่วน % ของ PO (4 แถบซ้อนกัน) ──────────────────────────────
// สเกลรวมยืดหยุ่นตามข้อมูลจริง: ปกติ 0-100% ของ PO (แท่งเต็มพอดีกรอบ) — ถ้าขาดทุนจริง
// จะขยายสเกลออกไปพอดีกับส่วนที่ทะลุเส้น PO เท่านั้น (บวก buffer เล็กน้อยกันชนขอบ)
// ทำให้กราฟยืดเต็มกรอบเสมอไม่ว่าจะมีขาดทุนหรือไม่ก็ตาม
function chartSegWidth(valuePct, maxPct) {
  const clamped = Math.max(0, Math.min(valuePct, maxPct));
  return `${(clamped / maxPct) * 100}%`;
}

function BarAxis({ maxPct }) {
  return (
    <Box sx={{ position: 'relative', height: 10 }}>
      {[50, 70, 100].map((p) => (
        <Typography
          key={p}
          variant="caption"
          sx={{
            position: 'absolute',
            left: `${(p / maxPct) * 100}%`,
            // ป้าย 100% อยู่ชิดขอบขวาสุดพอดี (เมื่อไม่ขาดทุน) — ชิดขวาแทนกึ่งกลาง กันตัวหนังสือ
            // ล้นออกนอกกรอบ ป้ายอื่น (50/70%) ยังกึ่งกลางเหมือนเดิมเพราะมีที่ว่างรอบตัวพอ
            transform: p === 100 ? 'translateX(-100%)' : 'translateX(-50%)',
            fontSize: '0.62rem',
            lineHeight: 1,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
          }}
        >
          {p}% PO
        </Typography>
      ))}
    </Box>
  );
}

function BarSeg({ widthPct, maxPct, color, textColor, children, minPctToShowLabel = 9 }) {
  return (
    <Box
      sx={{
        width: chartSegWidth(widthPct, maxPct),
        bgcolor: color,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {widthPct >= minPctToShowLabel && (
        <Typography
          noWrap
          sx={{ fontSize: '0.62rem', lineHeight: 1, color: textColor, px: 0.25, fontWeight: 600 }}
        >
          {children}
        </Typography>
      )}
    </Box>
  );
}

export function POProgressBars({
  po,
  receivedCombined,
  billedCombined,
  poRemaining,
  paidVal,
  col17,
  totalBudget,
  targetProfit,
  realProfit,
}) {
  const pctOf = (v) => (po > 0 ? (v / po) * 100 : 0);
  const fmtSeg = (label, v) => `${label} ${fmt(v)} (${pctOf(v).toFixed(1)}%)`;

  const receivedPct = pctOf(receivedCombined);
  const billedPct = pctOf(billedCombined);
  const remainPct = pctOf(poRemaining);

  // แถบ 3 = PAID + ต้องจ่ายอีกทั้งหมด(col17) + กำไรคาดการณ์ — 3 ก้อนนี้รวมกัน = PO เป๊ะ
  // (คนละสูตรกับ BG ที่ตั้งไว้/BG Remain now ซึ่งอาจติดลบเมื่อใช้เกินงบ เลยไม่เอามาแสดงตรงนี้)
  const paidPct = pctOf(paidVal);
  const col17Pct = pctOf(col17);
  const profitPct = pctOf(realProfit); // ติดลบได้ถ้าขาดทุน
  const isLoss3 = realProfit < 0;
  const seg3LeftPct = Math.min(paidPct + col17Pct, 100);

  const targetPct = pctOf(targetProfit);
  const isLoss = realProfit < 0;

  // แถว "แผน": BG ที่ตั้งไว้ + Profit Target — 2 ก้อนนี้รวมกัน = PO (เทียบคู่กับแถว "จริง"
  // ด้านล่าง: PAID + ต้องจ่ายอีก + Profit EST ซึ่งก็รวมกัน = PO เหมือนกัน)
  const bgPct = pctOf(totalBudget);

  // สเกลรวม: ปกติ 100% (แท่งยืดเต็มกรอบพอดี) — ถ้าขาดทุนจริง ขยายสเกลออกไปพอดีกับ
  // ส่วนที่ทะลุเส้น PO เท่านั้น (+3% buffer กันข้อความชนขอบ)
  const maxPct = isLoss ? 100 + Math.abs(profitPct) + 3 : 100;
  const poLineLeftPct = (100 / maxPct) * 100;
  const scale = (valuePct) => chartSegWidth(valuePct, maxPct);

  return (
    <Box sx={{ mb: 1.5 }}>
      <BarAxis maxPct={maxPct} />
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${poLineLeftPct}%`,
            width: '1px',
            bgcolor: '#94a3b8',
            zIndex: 2,
          }}
        />

        {/* เส้นอ้างอิง 50% (เทาเข้ม) / 70% (แดง) ของ PO — ทะลุทุกแถบลงมา */}
        {[
          { pct: 50, color: '#4b5563' },
          { pct: 70, color: '#dc2626' },
        ].map(({ pct: refPct, color: refColor }) => (
          <Box
            key={refPct}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(refPct / maxPct) * 100}%`,
              width: 0,
              borderLeft: `2px dashed ${refColor}`,
              zIndex: 3,
            }}
          />
        ))}

        {/* แถบ 1: เงินที่ได้รับ/รอรับ เทียบ PO */}
        <Stack direction="row" sx={{ height: 12, borderRadius: 0.5, overflow: 'hidden', mb: 1.1 }}>
          <BarSeg widthPct={receivedPct} maxPct={maxPct} color="#16a34a" textColor="#fff">
            {fmtSeg('รับเงินมาแล้ว', receivedCombined)}
          </BarSeg>
          <BarSeg widthPct={billedPct} maxPct={maxPct} color="#86efac" textColor="#ff00ff">
            {fmtSeg('Invoiced', billedCombined)}
          </BarSeg>
          <BarSeg widthPct={remainPct} maxPct={maxPct} color="#9ca3af" textColor="#fff">
            {fmtSeg('ยอดค้างส่งงานจาก PO', poRemaining)}
          </BarSeg>
        </Stack>

        {/* แถบ 2: PO เต็มแถบ */}
        <Box
          sx={{
            height: 12,
            borderRadius: 0.5,
            bgcolor: '#2563eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 0.25,
            width: scale(100),
          }}
        >
          <Typography sx={{ fontSize: '0.65rem', lineHeight: 1, color: '#fff', fontWeight: 700 }}>
            PO {fmt(po)}
          </Typography>
        </Box>

        {/* แถบ 3: แผน = BG ที่ตั้งไว้ + Profit Target — 2 ก้อนรวมกัน = PO เป๊ะ (เทียบคู่กับ
            แถบ 4 ด้านล่างที่เป็นของจริง: PAID + ต้องจ่ายอีก + Profit EST ซึ่งก็รวมกัน = PO เหมือนกัน) */}
        <Box sx={{ height: 12, position: 'relative', mb: 1.1, borderRadius: 0.5, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              width: scale(bgPct),
              height: '100%',
              bgcolor: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {bgPct >= 9 && (
              <Typography noWrap sx={{ fontSize: '0.62rem', lineHeight: 1, color: '#fff', fontWeight: 600 }}>
                {fmtSeg('BG ที่ตั้งไว้', totalBudget)}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: scale(bgPct),
              width: scale(targetPct),
              height: '100%',
              bgcolor: '#3b82f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {targetPct >= 9 && (
              <Typography noWrap sx={{ fontSize: '0.62rem', lineHeight: 1, color: '#fff', fontWeight: 600 }}>
                {fmtSeg('Profit Target', targetProfit)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* แถบ 4: จริง = PAID + ต้องจ่ายอีกทั้งหมด(col17) + กำไรคาดการณ์ — ขาดทุนจะทะลุเส้น PO ออกไป */}
        <Box sx={{ height: 12, position: 'relative', borderRadius: 0.5, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              width: scale(paidPct),
              height: '100%',
              bgcolor: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {paidPct >= 9 && (
              <Typography noWrap sx={{ fontSize: '0.62rem', lineHeight: 1, color: '#fff', fontWeight: 600 }}>
                {fmtSeg('PAID', paidVal)}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: scale(paidPct),
              width: scale(col17Pct),
              height: '100%',
              bgcolor: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {col17Pct >= 9 && (
              <Typography noWrap sx={{ fontSize: '0.62rem', lineHeight: 1, color: '#fff', fontWeight: 600 }}>
                {fmtSeg('ต้องจ่ายอีกทั้งหมด', col17)}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              left: scale(seg3LeftPct),
              width: scale(Math.abs(profitPct)),
              height: '100%',
              bgcolor: isLoss3 ? '#dc2626' : '#86efac',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {Math.abs(profitPct) >= 9 && (
              <Typography
                noWrap
                sx={{ fontSize: '0.62rem', lineHeight: 1, color: isLoss3 ? '#fff' : '#ff00ff', fontWeight: 600 }}
              >
                {fmtSeg(isLoss3 ? 'ขาดทุน' : 'Profit EST', realProfit)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
